import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ieltsApi, ieltsSpeakingApi } from '@/api/ielts';
import type { CEFRProgress } from '@/api/trainers';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useAiSkills } from '@/hooks/useAiSkills';
import { entryHidden } from '@/lib/aiEntries';
import { cn } from '@/lib/utils';

import { StudentSideWidgets } from './StudentSideWidgets';

type Attempt = {
  id: string;
  kind: 'writing' | 'speaking';
  band?: number;
  at: string;
};

/**
 * Правая колонка тренажёров (макет «Desktop Web v2»): последние попытки и то,
 * над чем стоит поработать.
 *
 * ⚠️ В макете во второй карточке — связный совет от ИИ («ты запинаешься перед
 * связками»). Такого разбора у нас никто не составляет, а сочинять его текстом
 * нельзя: это выглядело бы как настоящий вывод о человеке. Показываем то, что
 * посчитано на самом деле, — типы заданий с низкой точностью.
 */
export function TrainersSidebar({
  weak,
  marathonIds,
}: {
  weak?: CEFRProgress['weak'];
  marathonIds: string[];
}) {
  const { t, i18n } = useTranslation();

  const writing = useQuery({ queryKey: ['ielts', 'writing', 'submissions'], queryFn: ieltsApi.listSubmissions });
  // ⚠️ Тренажёр речи спрятан (D22, 11.09.2026): его попытки не показываем — строка
  // вела бы в закрытый вход. Запрос тоже не отправляем.
  useAppConfig();
  // Входы тренажёров закрывают и тумблеры навыков (docs/CONTRACTS/ai-skills.md).
  useAiSkills();
  const speakingShown = !entryHidden('/app/trainers/ielts/speaking');
  const speaking = useQuery({
    queryKey: ['ielts', 'speaking', 'sessions'],
    queryFn: ieltsSpeakingApi.listSessions,
    enabled: speakingShown,
  });

  const attempts: Attempt[] = [
    ...(writing.data ?? []).map((s) => ({ id: s.id, kind: 'writing' as const, band: s.band_overall, at: s.created_at })),
    ...(speakingShown ? speaking.data ?? [] : []).map((s) => ({ id: s.id, kind: 'speaking' as const, band: s.band_overall, at: s.created_at })),
  ]
    .filter((a) => a.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 3);

  return (
    <div className="space-y-3">
      {/* ⚠️ ТЕ ЖЕ ВИДЖЕТЫ, ЧТО В ОСТАЛЬНЫХ РАЗДЕЛАХ, И ИМЕННО ОБЩИМ КОДОМ.
          07.09.2026 заказчик увидел здесь одинокую карточку-подсказку и сказал:
          виджеты имел в виду такие же, как в других секциях. Копировать их сюда
          было бы быстрее, но копии расходятся — правка досталась бы одному
          разделу и обошла второй. */}
      <StudentSideWidgets marathonIds={marathonIds} />

      {/* ── Последние попытки ───────────────────────────────────────────── */}
      {attempts.length > 0 && (
        <div className="rounded-[22px] bg-surface-container px-5 py-[18px]">
          <h3 className="mb-[13px] text-[13.5px] font-bold text-on-surface">{t('trainersHub.lastSessions')}</h3>
          <div className="flex flex-col gap-3">
            {attempts.map((a) => (
              <Link
                key={a.id}
                to={
                  a.kind === 'speaking'
                    ? '/app/trainers/ielts/speaking'
                    : // Тренажёр письма закрыт (D26) — ведём на страницу письма, она осталась.
                      entryHidden('/app/trainers/ielts/writing')
                      ? '/app/ielts/writing'
                      : '/app/trainers/ielts/writing'
                }
                className="flex items-center gap-[11px]"
              >
                {/* Оценка кружком. Без оценки (работа ещё проверяется) —
                    прочерк, а не ноль: ноль читался бы как плохой балл. */}
                <span
                  className={cn(
                    'grid h-[38px] w-[38px] flex-none place-items-center rounded-full text-[12px] font-bold',
                    a.kind === 'writing' ? 'bg-info-container text-info' : 'bg-primary-container text-on-primary-container',
                  )}
                >
                  {a.band != null ? a.band.toFixed(1) : '—'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-semibold text-on-surface">
                    {t(a.kind === 'writing' ? 'trainersHub.ieltsWriting' : 'trainersHub.ieltsSpeaking')}
                  </span>
                  <span className="block text-[11px] text-on-surface-variant">
                    {new Date(a.at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })}
                    {a.band == null ? ` · ${t('trainersHub.beingChecked')}` : ''}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Над чем поработать ──────────────────────────────────────────── */}
      {!!weak?.length && (
        <div className="rounded-[22px] bg-info-container px-5 py-[18px]">
          <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-info">
            {t('trainersHub.weakTitle')}
          </div>
          <p className="mt-2.5 text-[13px] leading-[1.6] text-on-surface-variant">{t('trainersHub.weakHint')}</p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {weak.map((w) => (
              <span key={w.block_type} className="rounded-full bg-surface-container-lowest px-2.5 py-1 text-[11.5px] font-semibold text-on-surface">
                {t(`trainersHub.skill.${w.skill}`)} · {Math.round(w.accuracy * 100)}%
              </span>
            ))}
          </div>
          <Link
            to="/app/trainers/drill"
            className="mt-[13px] inline-flex rounded-full bg-info px-4 py-[9px] text-[12.5px] font-bold text-white transition hover:brightness-110"
          >
            {t('trainersHub.drillWeak')}
          </Link>
        </div>
      )}

    </div>
  );
}
